import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { ArrowLeft, FileCheck, Users, CreditCard, Ban, RefreshCw } from "lucide-react";

export default function Terms() {
  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 font-mono text-sm text-zinc-500 hover:text-primary transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          VOLVER
        </Link>

        <div className="bg-[#111] border border-zinc-800 p-8">
          <div className="flex items-center gap-3 mb-8">
            <FileCheck className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-black tracking-tighter">
              TÉRMINOS Y <span className="text-primary">CONDICIONES</span>
            </h1>
          </div>

          <p className="text-zinc-400 mb-8 font-mono text-sm">
            Última actualización: {new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>

          {/* Introducción */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-white mb-4">1. INTRODUCCIÓN</h2>
            <div className="text-zinc-400 space-y-2">
              <p>
                Estos Términos y Condiciones regulan el uso de la plataforma Rockmaps, 
                incluyendo el sitio web y la aplicación móvil (en adelante, "la Plataforma").
              </p>
              <p>
                Al registrarte o usar la Plataforma, aceptas estos términos en su totalidad. 
                Si no estás de acuerdo con alguna parte, no debes usar nuestros servicios.
              </p>
            </div>
          </section>

          {/* Tipos de usuario */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              2. TIPOS DE USUARIO
            </h2>
            <div className="text-zinc-400 space-y-4">
              <div className="bg-[#0a0a0a] border border-zinc-800 p-4">
                <h3 className="text-white font-bold mb-2">USUARIOS (Rockers)</h3>
                <p>Personas que utilizan la Plataforma para descubrir eventos y bares.</p>
                <ul className="list-disc list-inside mt-2 ml-4 text-sm">
                  <li>Registro gratuito</li>
                  <li>Acceso a listado de eventos y bares</li>
                  <li>Posibilidad de guardar eventos favoritos</li>
                  <li>Configurar alertas de entradas</li>
                </ul>
              </div>

              <div className="bg-[#0a0a0a] border border-zinc-800 p-4">
                <h3 className="text-white font-bold mb-2">BARES</h3>
                <p>Propietarios o gestores de locales de música.</p>
                <ul className="list-disc list-inside mt-2 ml-4 text-sm">
                  <li>Requieren aprobación previa del administrador</li>
                  <li>Pueden publicar eventos y gestionar su perfil</li>
                  <li>Disponibles planes gratuito (Garage) y de pago (Backstage)</li>
                </ul>
              </div>

              <div className="bg-[#0a0a0a] border border-zinc-800 p-4">
                <h3 className="text-white font-bold mb-2">BANDAS</h3>
                <p>Músicos y grupos que buscan actuaciones.</p>
                <ul className="list-disc list-inside mt-2 ml-4 text-sm">
                  <li>Registro gratuito</li>
                  <li>Pueden crear perfil y recibir solicitudes de bares</li>
                  <li>Disponibles planes gratuito (Garage) y de pago (Stage)</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Registro y cuenta */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-white mb-4">3. REGISTRO Y CUENTA</h2>
            <div className="text-zinc-400 space-y-2">
              <p>Para crear una cuenta debes:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Ser mayor de 18 años</li>
                <li>Proporcionar información veraz y actualizada</li>
                <li>Mantener la confidencialidad de tus credenciales</li>
                <li>Notificar cualquier uso no autorizado de tu cuenta</li>
              </ul>
              <p className="mt-4">
                Eres responsable de todas las actividades que ocurran bajo tu cuenta.
              </p>
            </div>
          </section>

          {/* Suscripciones y pagos */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-primary" />
              4. SUSCRIPCIONES Y PAGOS
            </h2>
            <div className="text-zinc-400 space-y-4">
              <div className="bg-[#0a0a0a] border border-zinc-800 p-4">
                <h3 className="text-white font-bold mb-2">Planes de pago</h3>
                <ul className="space-y-2">
                  <li><strong className="text-zinc-300">Bares - Plan Backstage:</strong> 29€/mes</li>
                  <li><strong className="text-zinc-300">Bandas - Plan Stage:</strong> 9€/mes</li>
                </ul>
              </div>

              <p><strong className="text-zinc-300">Facturación:</strong> Los pagos se realizan de forma mensual a través de Stripe.</p>
              
              <p><strong className="text-zinc-300">Renovación automática:</strong> Las suscripciones se renuevan automáticamente. Puedes cancelar en cualquier momento desde tu panel de control.</p>
              
              <p><strong className="text-zinc-300">Cancelación:</strong> Al cancelar, mantendrás el acceso hasta el final del período facturado.</p>
              
              <div className="bg-yellow-500/10 border border-yellow-500/30 p-4 mt-4">
                <p className="text-yellow-400 font-mono text-sm">
                  <strong>Política de reembolso:</strong> No se realizan reembolsos por períodos parciales. 
                  Si cancelas, tu suscripción seguirá activa hasta el final del mes pagado.
                </p>
              </div>
            </div>
          </section>

          {/* Contenido del usuario */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-white mb-4">5. CONTENIDO DEL USUARIO</h2>
            <div className="text-zinc-400 space-y-2">
              <p>Al publicar contenido en la Plataforma (descripciones, imágenes, eventos):</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Garantizas que tienes los derechos necesarios sobre dicho contenido</li>
                <li>Otorgas a Rockmaps una licencia no exclusiva para mostrar el contenido</li>
                <li>Eres responsable de la veracidad de la información publicada</li>
                <li>Te comprometes a no publicar contenido ilegal, ofensivo o que infrinja derechos de terceros</li>
              </ul>
              <p className="mt-4">
                Nos reservamos el derecho de eliminar contenido que viole estos términos.
              </p>
            </div>
          </section>

          {/* Prohibiciones */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Ban className="w-5 h-5 text-primary" />
              6. CONDUCTAS PROHIBIDAS
            </h2>
            <div className="text-zinc-400 space-y-2">
              <p>Está prohibido:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Crear cuentas falsas o suplantar identidad</li>
                <li>Publicar eventos falsos o información engañosa</li>
                <li>Usar la Plataforma para spam o publicidad no autorizada</li>
                <li>Intentar acceder a sistemas o datos de otros usuarios</li>
                <li>Realizar scraping o extracción automatizada de datos</li>
                <li>Revender o comercializar acceso a la Plataforma</li>
                <li>Publicar contenido que incite al odio o la violencia</li>
              </ul>
            </div>
          </section>

          {/* Suspensión de cuenta */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-white mb-4">7. SUSPENSIÓN Y TERMINACIÓN</h2>
            <div className="text-zinc-400 space-y-2">
              <p>Podemos suspender o terminar tu cuenta si:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Violas estos Términos y Condiciones</li>
                <li>Realizas actividades fraudulentas</li>
                <li>Recibes múltiples quejas de otros usuarios</li>
                <li>No pagas las suscripciones correspondientes</li>
              </ul>
              <p className="mt-4">
                En caso de suspensión por incumplimiento, no tendrás derecho a reembolso.
              </p>
            </div>
          </section>

          {/* Modificaciones */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-primary" />
              8. MODIFICACIONES
            </h2>
            <div className="text-zinc-400 space-y-2">
              <p>
                Podemos modificar estos Términos en cualquier momento. Te notificaremos 
                cambios significativos por email o mediante un aviso en la Plataforma.
              </p>
              <p>
                El uso continuado de la Plataforma tras las modificaciones implica la 
                aceptación de los nuevos términos.
              </p>
            </div>
          </section>

          {/* Limitación de responsabilidad */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-white mb-4">9. LIMITACIÓN DE RESPONSABILIDAD</h2>
            <div className="text-zinc-400 space-y-2">
              <p>
                La Plataforma se proporciona "tal cual". No garantizamos:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Disponibilidad ininterrumpida del servicio</li>
                <li>Que los eventos publicados se celebren según lo anunciado</li>
                <li>La calidad de los servicios ofrecidos por bares o bandas</li>
              </ul>
              <p className="mt-4">
                En la máxima medida permitida por la ley, Rockmaps no será responsable 
                de daños indirectos, incidentales o consecuentes derivados del uso de la Plataforma.
              </p>
            </div>
          </section>

          {/* Ley aplicable */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-white mb-4">10. LEY APLICABLE</h2>
            <div className="text-zinc-400 space-y-2">
              <p>
                Estos Términos se rigen por la legislación española. Cualquier disputa 
                se resolverá en los tribunales de Barcelona, España.
              </p>
            </div>
          </section>

          {/* Contacto */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">11. CONTACTO</h2>
            <div className="text-zinc-400">
              <p>
                Para consultas sobre estos Términos y Condiciones:{" "}
                <a href="mailto:info@rockmaps.es" className="text-primary hover:underline">
                  info@rockmaps.es
                </a>
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
